import { useRef, useState } from 'react';
import {
    ArrowUpTrayIcon,
    PaperClipIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import pdfIcon from '../../../assets/chat/icon-pdf.png';
import wordIcon from '../../../assets/chat/icon-word.png';
import {
    TASK_UPLOAD_ACCEPT,
    TASK_UPLOAD_HINT,
    TASK_MAX_ATTACHMENTS,
    formatFileSize,
    getAttachmentKind,
    ATTACHMENT_KIND_LABELS,
    isImageAttachment,
    asAttachmentList,
} from '../../../common/taskUploads';
import './task-form.css';

const FILE_KIND_ICONS = {
    pdf: pdfIcon,
    word: wordIcon,
    excel: wordIcon,
    powerpoint: wordIcon,
    file: wordIcon,
};

export const TaskAttachmentField = ({
    label = 'Archivo adjunto',
    existing = null,
    onRemoveExisting = null,
    staged = null,
    isUploading = false,
    onSelectFile,
    onSelectFiles,
    onClearStaged,
    onRemoveStaged,
    disabled = false,
    multiple = false,
    maxFiles = TASK_MAX_ATTACHMENTS,
    hint = TASK_UPLOAD_HINT,
    accept = TASK_UPLOAD_ACCEPT,
}) => {
    const inputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);

    const existingList = asAttachmentList(existing);
    const stagedList = asAttachmentList(staged);
    const limit = multiple ? maxFiles : 1;
    const occupied = existingList.length + stagedList.filter((item) => item.status !== 'error').length;
    const remaining = Math.max(0, limit - occupied);
    const canAddMore = remaining > 0 && !disabled;

    const handleFiles = (fileList) => {
        const files = Array.from(fileList || []).filter(Boolean);
        if (!files.length || disabled) return;
        if (multiple) {
            onSelectFiles?.(files);
            return;
        }
        onSelectFile?.(files[0]);
    };

    const handleFileChange = (e) => {
        handleFiles(e.target.files);
        e.target.value = '';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        if (canAddMore) setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (!canAddMore) return;
        handleFiles(e.dataTransfer.files);
    };

    const renderFileVisual = ({ mimeType, previewUrl = null, url = null, progress = null }) => {
        const kind = getAttachmentKind(mimeType);
        const imageSrc = previewUrl || (kind === 'image' && url ? url : null);

        if (kind === 'image' && imageSrc) {
            return (
                <div className="task-attachment__preview">
                    <img src={imageSrc} alt="" width={72} height={72} />
                    {progress !== null ? (
                        <span className="task-attachment__preview-overlay" aria-hidden="true">
                            {progress}%
                        </span>
                    ) : null}
                </div>
            );
        }

        const iconSrc = FILE_KIND_ICONS[kind] || FILE_KIND_ICONS.file;

        return (
            <span className="task-attachment__file-icon-wrap">
                <img
                    src={iconSrc}
                    alt=""
                    className="task-attachment__file-badge"
                    width={26}
                    height={26}
                    aria-hidden="true"
                />
                {progress !== null ? (
                    <span className="task-attachment__file-icon-overlay" aria-hidden="true">
                        {progress}%
                    </span>
                ) : null}
            </span>
        );
    };

    const renderFileCard = ({
        fileName,
        size,
        mimeType,
        url = null,
        previewUrl = null,
        progress = null,
        error = null,
        onRemove,
    }) => {
        const kind = getAttachmentKind(mimeType);
        const showProgress = progress !== null;

        return (
            <div
                className={`task-attachment__file${error ? ' task-attachment__file--error' : ''}`}
                data-uploading={showProgress ? 'true' : 'false'}
                data-kind={kind}
            >
                {renderFileVisual({ mimeType, previewUrl, url, progress })}
                <div className="task-attachment__file-body">
                    {url ? (
                        <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="task-attachment__file-name task-attachment__file-name--link"
                        >
                            {fileName}
                        </a>
                    ) : (
                        <p className="task-attachment__file-name">{fileName}</p>
                    )}
                    <p className="task-attachment__file-meta">
                        {error || [
                            ATTACHMENT_KIND_LABELS[kind],
                            size ? formatFileSize(size) : '',
                        ].filter(Boolean).join(' · ')}
                    </p>
                    {showProgress ? (
                        <>
                            <div className="task-attachment__progress-header">
                                <p className="task-attachment__progress-label">Subiendo archivo…</p>
                                <p className="task-attachment__progress-value">{progress}%</p>
                            </div>
                            <div
                                className="task-attachment__progress"
                                role="progressbar"
                                aria-valuenow={progress}
                                aria-valuemin={0}
                                aria-valuemax={100}
                                aria-label={`Progreso de subida: ${progress}%`}
                            >
                                <div
                                    className="task-attachment__progress-fill"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </>
                    ) : null}
                </div>
                {onRemove && !disabled ? (
                    <button
                        type="button"
                        onClick={onRemove}
                        className="task-attachment__remove"
                        aria-label={showProgress ? 'Cancelar subida' : 'Quitar archivo'}
                        title={showProgress ? 'Cancelar subida' : 'Quitar archivo'}
                    >
                        <XMarkIcon className="w-4 h-4" aria-hidden="true" />
                    </button>
                ) : null}
            </div>
        );
    };

    const dropzone = canAddMore || (!existingList.length && !stagedList.length) ? (
        <button
            type="button"
            onClick={() => canAddMore && inputRef.current?.click()}
            disabled={!canAddMore}
            className="task-attachment__dropzone"
            data-dragging={isDragging ? 'true' : 'false'}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <span className="task-attachment__dropzone-visual" aria-hidden="true">
                <ArrowUpTrayIcon className="task-attachment__dropzone-icon" />
            </span>
            <span>
                {multiple ? 'Arrastra archivos o selecciónalos' : 'Arrastra un archivo o selecciónalo'}
            </span>
            <span className="task-attachment__dropzone-sub">
                <PaperClipIcon className="inline w-3.5 h-3.5 -mt-px mr-1" aria-hidden="true" />
                {hint}
            </span>
        </button>
    ) : null;

    const showBottomHint = multiple || !dropzone;

    return (
        <div className="task-attachment">
            {label ? (
                <span className="task-attachment__label">{label}</span>
            ) : null}

            <div className="task-attachment__stack">
                {existingList.map((file, index) => (
                    <div key={`existing-${file.storageKey || file.url || file.fileName}-${index}`}>
                        {renderFileCard({
                            fileName: file.fileName,
                            size: file.size,
                            mimeType: file.mimeType,
                            url: file.url,
                            previewUrl: isImageAttachment(file.mimeType) ? file.url : null,
                            onRemove: onRemoveExisting
                                ? () => onRemoveExisting(multiple ? file : undefined, index)
                                : null,
                        })}
                    </div>
                ))}

                {stagedList.map((file) => (
                    <div key={file.id || file.fileName}>
                        {renderFileCard({
                            fileName: file.fileName,
                            size: file.size,
                            mimeType: file.mimeType,
                            previewUrl: file.previewUrl,
                            progress: file.status === 'uploading' || isUploading ? file.progress : null,
                            error: file.error || null,
                            onRemove: () => (onRemoveStaged
                                ? onRemoveStaged(file.id)
                                : onClearStaged?.()),
                        })}
                    </div>
                ))}

                {dropzone}
            </div>

            {showBottomHint ? (
                <p className="task-attachment__hint">
                    {hint}
                    {multiple ? ` · ${occupied} / ${limit}` : ''}
                </p>
            ) : null}

            <input
                ref={inputRef}
                type="file"
                className="sr-only"
                accept={accept}
                multiple={multiple}
                onChange={handleFileChange}
                tabIndex={-1}
            />
        </div>
    );
};
